// BARCINO - Page vision : drawer fondateur + sommaire flottant (scroll-spy)
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const barcinoVision = {
    _trigger_handlers: [],
    _close_handlers: [],
    _key_handler: null,
    _anchor_root: null,
    _anchor_sections: [],
    _anchor_toggle_handlers: [],
    _anchor_link_handlers: [],
    _anchor_observer: null,
    _anchor_scroll_handler: null,
    _anchor_resize_handler: null,
    _anchor_raf: null,

    init() {
        this.destroy();

        this._initFounderDrawer();
        this._initAnchor();
        this._initHeroParallax();
    },

    // Léger parallax sur les deux photos du hero (effet "speed" à la
    // Locomotive Scroll) pour que les cadres ne restent pas figés au scroll —
    // rotation reprise en GSAP pour coexister avec le déplacement vertical
    // dans la même matrice de transform (sinon la rotation CSS est écrasée).
    _initHeroParallax() {
        const imgA = document.querySelector('.bc-vision-hero__image--a');
        const imgB = document.querySelector('.bc-vision-hero__image--b');
        if (!imgA && !imgB) return;

        const trigger = {
            trigger: '.bc-vision-hero',
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
        };

        if (imgA) {
            gsap.fromTo(imgA,
                { y: 0, rotation: -8 },
                { y: -80, rotation: -8, ease: 'none', scrollTrigger: trigger }
            );
        }
        if (imgB) {
            gsap.fromTo(imgB,
                { y: 0, rotation: 8 },
                { y: 50, rotation: 8, ease: 'none', scrollTrigger: { ...trigger } }
            );
        }
    },

    _initFounderDrawer() {
        const drawer = document.querySelector('[data-founder-drawer]');
        if (!drawer) return;

        const triggers = document.querySelectorAll('[data-founder-trigger]');
        const panels = document.querySelectorAll('[data-founder-panel]');
        const closeButtons = document.querySelectorAll('[data-founder-close]');

        const openFounder = (founderId, side) => {
            if (!founderId) return;

            drawer.classList.remove('side-left', 'side-right');
            drawer.classList.add(side === 'left' ? 'side-left' : 'side-right');
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.documentElement.classList.add('is-founder-drawer-open');
            document.body.classList.add('is-founder-drawer-open');

            panels.forEach((panel) => {
                panel.classList.toggle('is-active', panel.getAttribute('data-founder-panel') === founderId);
            });

            // Le panneau part de visibility:hidden (backdrop-filter actif) : sans forcer
            // un reflow ici, le navigateur peut figer le transform sur sa valeur fermée
            // et le panneau apparaît hors-écran au lieu de glisser depuis son côté.
            drawer.style.display = 'none';
            void drawer.offsetHeight;
            drawer.style.display = '';
        };

        const closeFounder = () => {
            drawer.classList.remove('is-open');
            drawer.setAttribute('aria-hidden', 'true');
            document.documentElement.classList.remove('is-founder-drawer-open');
            document.body.classList.remove('is-founder-drawer-open');
        };

        triggers.forEach((trigger) => {
            const handler = () => openFounder(
                trigger.getAttribute('data-founder-id'),
                trigger.getAttribute('data-founder-side')
            );
            const keyHandler = (event) => {
                if (event.target !== trigger || !['Enter', ' '].includes(event.key)) return;
                event.preventDefault();
                handler();
            };
            trigger.addEventListener('click', handler);
            trigger.addEventListener('keydown', keyHandler);
            this._trigger_handlers.push({ el: trigger, type: 'click', fn: handler });
            this._trigger_handlers.push({ el: trigger, type: 'keydown', fn: keyHandler });
        });

        closeButtons.forEach((button) => {
            button.addEventListener('click', closeFounder);
            this._close_handlers.push({ el: button, type: 'click', fn: closeFounder });
        });

        const overlayClick = (event) => {
            if (event.target === drawer) closeFounder();
        };
        drawer.addEventListener('click', overlayClick);
        this._close_handlers.push({ el: drawer, type: 'click', fn: overlayClick });

        this._key_handler = (event) => {
            if (event.key === 'Escape') closeFounder();
        };
        document.addEventListener('keydown', this._key_handler);
    },

    // Sommaire flottant — même logique que barcino_faq.js (scroll-spy des
    // sections via IntersectionObserver, visibilité selon position de scroll)
    _initAnchor() {
        this._anchor_root = document.querySelector('.bc-vision-anchor');
        if (!this._anchor_root) return;

        const root = this._anchor_root;
        const toggleBtns = root.querySelectorAll('[data-vision-anchor-toggle]');
        const panel = root.querySelector('[data-vision-anchor-panel]');
        const links = root.querySelectorAll('[data-vision-anchor-link]');
        const indexEl = root.querySelector('[data-vision-anchor-index]');
        const labelEl = root.querySelector('[data-vision-anchor-label]');
        this._anchor_sections = Array.from(links)
            .map((link) => document.getElementById(link.dataset.target))
            .filter(Boolean);

        const setOpen = (open) => {
            root.classList.toggle('is-open', open);
            toggleBtns.forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
            panel?.setAttribute('aria-hidden', String(!open));
            // inert retire aussi les liens du panneau replié du parcours clavier
            // (sans quoi aria-hidden + descendants focusables = erreur d'accessibilité).
            panel?.toggleAttribute('inert', !open);
        };

        const setActive = (link) => {
            const index = Array.from(links).indexOf(link) + 1;
            if (indexEl) indexEl.textContent = String(index).padStart(2, '0');
            if (labelEl) labelEl.textContent = link.querySelector('.bc-vision-anchor__item-label')?.textContent ?? '';

            links.forEach((item) => {
                const active = item === link;
                item.classList.toggle('is-active', active);
                if (active) {
                    item.setAttribute('aria-current', 'true');
                } else {
                    item.removeAttribute('aria-current');
                }
            });
        };

        toggleBtns.forEach((btn) => {
            const handler = () => setOpen(!root.classList.contains('is-open'));
            btn.addEventListener('click', handler);
            this._anchor_toggle_handlers.push({ el: btn, fn: handler });
        });

        links.forEach((link) => {
            const handler = (event) => {
                event.preventDefault();
                const target = document.getElementById(link.dataset.target);
                setOpen(false);
                if (!target) return;

                const offset = 120;
                const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top, behavior: 'smooth' });
            };
            link.addEventListener('click', handler);
            this._anchor_link_handlers.push({ el: link, fn: handler });
        });

        if (this._anchor_sections.length && 'IntersectionObserver' in window) {
            this._anchor_observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const link = root.querySelector(`[data-target="${entry.target.id}"]`);
                    if (link) setActive(link);
                });
            }, { rootMargin: '-30% 0px -55% 0px', threshold: 0 });

            this._anchor_sections.forEach((section) => this._anchor_observer.observe(section));
        }

        const updateVisibility = () => {
            if (!this._anchor_sections.length) return;

            const first = this._anchor_sections[0];
            const last = this._anchor_sections[this._anchor_sections.length - 1];
            const firstTop = first.getBoundingClientRect().top;
            const lastBottom = last.getBoundingClientRect().bottom;
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const isDesktop = window.matchMedia('(min-width: 993px)').matches;
            const visible = isDesktop && firstTop <= viewportHeight * .72 && lastBottom >= 160;

            root.classList.toggle('is-visible', visible);
            root.setAttribute('aria-hidden', String(!visible));
            // Tant que le sommaire est masqué, inert empêche que ses boutons
            // restent focusables au clavier (erreur aria-hidden-focus).
            root.toggleAttribute('inert', !visible);

            if (!visible) setOpen(false);
        };

        const requestVisibilityUpdate = () => {
            if (this._anchor_raf) return;
            this._anchor_raf = window.requestAnimationFrame(() => {
                this._anchor_raf = null;
                updateVisibility();
            });
        };

        this._anchor_scroll_handler = requestVisibilityUpdate;
        this._anchor_resize_handler = requestVisibilityUpdate;
        window.addEventListener('scroll', this._anchor_scroll_handler, { passive: true });
        window.addEventListener('resize', this._anchor_resize_handler);
        requestVisibilityUpdate();
    },

    destroy() {
        ScrollTrigger.getAll().forEach(t => t.kill());
        gsap.set(['.bc-vision-hero__image--a', '.bc-vision-hero__image--b'], { clearProps: 'transform' });

        this._trigger_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._close_handlers.forEach(({ el, type, fn }) => el.removeEventListener(type, fn));
        this._trigger_handlers = [];
        this._close_handlers = [];

        if (this._key_handler) {
            document.removeEventListener('keydown', this._key_handler);
            this._key_handler = null;
        }

        document.documentElement.classList.remove('is-founder-drawer-open');
        document.body.classList.remove('is-founder-drawer-open');

        this._anchor_toggle_handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
        this._anchor_link_handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
        this._anchor_toggle_handlers = [];
        this._anchor_link_handlers = [];

        if (this._anchor_observer) {
            this._anchor_observer.disconnect();
            this._anchor_observer = null;
        }

        if (this._anchor_scroll_handler) window.removeEventListener('scroll', this._anchor_scroll_handler);
        if (this._anchor_resize_handler) window.removeEventListener('resize', this._anchor_resize_handler);
        if (this._anchor_raf) {
            window.cancelAnimationFrame(this._anchor_raf);
            this._anchor_raf = null;
        }

        this._anchor_scroll_handler = null;
        this._anchor_resize_handler = null;
        this._anchor_root = null;
        this._anchor_sections = [];
    },
};

export default barcinoVision;
